import {
  DEFAULT_ROWS_PER_PAGE,
  ExpandableTable,
  RoundedBox,
  SimpleSelect,
  TableCellType,
  TableSortType,
} from "@knowfully/ui.common";
import { useIntl } from "react-intl";
import useGlobalContext from "globalProviders/GlobalDataProvider";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import {
  CourseFilter,
  CourseModel,
  CourseStatus,
  ListRequestOfCourseFilter,
} from "services/facultyPortalClient";
import CoursesFilter from "./CoursesFilter";
import { Box, Link as MuiLink } from "@mui/material";
import { Link } from "react-router-dom";
import ClaimCreditButton from "pages/CoursesPage/ClaimCreditButton";

const styles = {
  courseTitle: {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
  },
  courseLink: {
    textDecoration: "none",
    color: "primary.dark",
    fontWeight: "bold",
  },
};

export default function CoursesPage() {
  const { formatMessage: t } = useIntl();
  const { courseService, courseDetailsUrl } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("eventStartDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [filter, setFilter] = useState({ searchText: "" } as CourseFilter);
  const [courseStatusFilter, setCourseStatusFilter] = useState(
    CourseStatus.Active
  );

  const { data, isFetching } = useQuery(
    [
      "getCourses",
      order,
      orderBy,
      page,
      rowsPerPage,
      filter,
      courseStatusFilter,
    ],
    () =>
      courseService.getCourses(
        courseStatusFilter,
        ListRequestOfCourseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: filter,
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [filter, courseStatusFilter]);

  const ClaimCredit = ({ value }: { value: CourseModel }) => {
    return <ClaimCreditButton course={value} />;
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "title",
      type: "node",
      label: t({ id: "courseTitle" }),
      View: ({ value }: { value: CourseModel }) => (
        <Box sx={styles.courseTitle}>
          <MuiLink
            component={Link}
            to={courseDetailsUrl(value.id)}
            sx={styles.courseLink}
          >
            {value.title}
          </MuiLink>
        </Box>
      ),
    },
    {
      accessor: "format",
      type: "text",
      label: t({ id: "format" }),
    },
    {
      accessor: "eventStartDate",
      type: "date",
      label: t({ id: "courseDate" }),
    },
    {
      accessor: "value",
      type: "node",
      noSort: true,
      label: t({ id: "claimCredit" }),
      View: ClaimCredit,
    },
  ];

  const courses = data?.data ?? [];

  const statusOptions = [
    { value: CourseStatus.Active, id: CourseStatus.Active },
    { value: CourseStatus.Inactive, id: CourseStatus.Inactive },
  ];

  const StatusSelect = (
    <SimpleSelect
      sx={{ width: 130 }}
      options={statusOptions}
      onChange={(v) => {
        setCourseStatusFilter(v as CourseStatus);
      }}
      label={t({ id: "courseStatus" })}
      value={courseStatusFilter}
    />
  );

  return (
    <>
      <RoundedBox>
        <CoursesFilter onChange={setFilter} />
      </RoundedBox>
      <RoundedBox>
        <ExpandableTable
          aria-label={t({ id: "myCourses" })}
          rowCount={data?.totalRecords ?? 0}
          rows={courses}
          cells={cells}
          page={page}
          setPage={setPage}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          loading={isFetching}
          collapsable={true}
          titleComponent={StatusSelect}
        />
      </RoundedBox>
    </>
  );
}
